import { api } from "@Aspecto/backend/convex/_generated/api";
import { useMutation } from "convex/react";
import { createFileRoute } from "@tanstack/react-router";
import { toast } from "sonner";

import { Label } from "@/components/ui/label";
import { SectionCard } from "@/components/ui/section-card";
import { Switch } from "@/components/ui/switch";
import { useCurrentUser } from "@/hooks/use-current-account";

export const Route = createFileRoute("/_authenticated/app/settings/notifications")({
  component: NotificationSettings,
});

function NotificationSettings() {
  const { data: user } = useCurrentUser();
  const updatePreferences = useMutation(api.users.updateNotificationPreferences);

  const eventReminders = user?.notifyEventReminders ?? true;
  const communityInvitations = user?.notifyCommunityInvitations ?? true;

  const handleToggle = async (
    field: "notifyEventReminders" | "notifyCommunityInvitations",
    value: boolean,
  ) => {
    try {
      await updatePreferences({
        notifyWeeklyDigest: user?.notifyWeeklyDigest ?? true,
        notifyEventReminders: field === "notifyEventReminders" ? value : eventReminders,
        notifyCommunityInvitations:
          field === "notifyCommunityInvitations" ? value : communityInvitations,
      });
    } catch {
      toast.error("Failed to update notification preferences.");
    }
  };

  return (
    <SectionCard title="Notifications" description="Choose what you want to be notified about">
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label htmlFor="event-reminders">Event reminders</Label>
            <p className="text-xs text-muted-foreground">
              Get notified before events you've RSVP'd to
            </p>
          </div>
          <Switch
            id="event-reminders"
            checked={eventReminders}
            onCheckedChange={(v) => handleToggle("notifyEventReminders", v)}
          />
        </div>
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label htmlFor="community-invitations">Community invitations</Label>
            <p className="text-xs text-muted-foreground">
              Get notified when you're invited to join a group
            </p>
          </div>
          <Switch
            id="community-invitations"
            checked={communityInvitations}
            onCheckedChange={(v) => handleToggle("notifyCommunityInvitations", v)}
          />
        </div>
      </div>
    </SectionCard>
  );
}
